import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions, storage } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { uploadClaimProof, friendlyUploadError } from '../utils/uploadImage';
import { friendlyCallError } from '../utils/reviews';
import AuthModal from './AuthModal';
import './ClaimRestaurant.css';

const POSITIONS = [
  { id: 'owner', label: 'Owner' },
  { id: 'manager', label: 'Manager' },
  { id: 'marketing', label: 'Marketing / social media' },
  { id: 'other', label: 'Something else' },
];

// The strip under a listing's details: "Own this restaurant? Claim it" on an
// unclaimed listing, or "You manage this restaurant" for its owner. Claiming
// needs a real account (not a guest) because approval makes that account the
// restaurant's owner. An admin reviews every claim (functions/claims.js).
export default function ClaimRestaurant({ restaurant, badge = false }) {
  const { user, userProfile } = useAuth();
  const [open, setOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);

  if (user && restaurant.ownerId === user.uid) {
    return (
      <p className="claim-strip claim-strip--owner">
        <span className="claim-strip__badge">✓ You manage this business</span>
        <Link to="/business/dashboard">Edit listing &amp; socials</Link>
      </p>
    );
  }
  if (restaurant.ownerId) return badge ? <span className="claim-badge claim-badge--claimed">&#10003; Claimed</span> : null;

  return (
    <>
      <p className={badge ? "claim-badge" : "claim-strip"}>
        {!badge && <>Own or manage {restaurant.name}?{' '}</>}
        <button type="button" className="claim-strip__link" onClick={() => (user ? setOpen(true) : setAuthOpen(true))}>
          {badge ? 'Unclaimed' : 'Claim Business'}
        </button>
      </p>
      {authOpen && (
        <AuthModal
          defaultTab="signup"
          heading="Sign in to claim this business"
          onClose={() => setAuthOpen(false)}
          onSuccess={() => setOpen(true)}
        />
      )}
      {open && user && (
        <ClaimDialog restaurant={restaurant} user={user} userProfile={userProfile} onClose={() => setOpen(false)} />
      )}
    </>
  );
}

export function ClaimDialog({ restaurant, user, userProfile, onClose }) {
  const overlayRef = useRef(null);
  const [existing, setExisting] = useState(undefined);
  const [form, setForm] = useState({
    contactName: userProfile?.displayName || user.displayName || '',
    position: 'owner',
    phone: '',
    businessEmail: user.email || '',
    note: '',
  });
  const { resendVerification } = useAuth();
  const [verified, setVerified] = useState(user.emailVerified);
  const [verifyMessage, setVerifyMessage] = useState('');
  const [proof, setProof] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getDoc(doc(db, 'businessClaims', `${restaurant.id}_${user.uid}`))
      .then((snap) => setExisting(snap.exists() ? snap.data() : null))
      .catch(() => setExisting(null));
  }, [restaurant.id, user.uid]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    let mounted = true;
    async function recheck() {
      try { await user.reload(); await user.getIdToken(true); if (mounted) setVerified(user.emailVerified); }
      catch (e) { if (mounted) setVerifyMessage(e.message); }
    }
    recheck();
    window.addEventListener('focus', recheck);
    return () => { mounted = false; window.removeEventListener('focus', recheck); };
  }, [user]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      let proofPath = null;
      if (proof) {
        try {
          proofPath = await uploadClaimProof(storage, auth, proof);
        } catch (err) {
          setError(friendlyUploadError(err));
          setBusy(false);
          return;
        }
      }
      const submitRestaurantClaim = httpsCallable(functions, 'submitRestaurantClaim');
      await submitRestaurantClaim({ businessId: restaurant.id, ...form, proofPath });
      setExisting({ status: 'pending' });
    } catch (err) {
      setError(friendlyCallError(err));
    } finally {
      setBusy(false);
    }
  }

  // The "your claim is being reviewed" / "this restaurant is yours" screens
  // are a short message and one button, so they centre; the form doesn't.
  const isStatusScreen = existing?.status === 'pending' || existing?.status === 'approved';

  let body;
  if (!verified) {
    body = <div className="claim-dialog__status"><p>Verify your email before claiming this business.</p><button type="button" className="claim-dialog__submit" onClick={async () => { try { await resendVerification(); setVerifyMessage('Verification email sent. Open the link, then return here.'); } catch (e) { setVerifyMessage(e.message); } }}>Resend verification email</button><p role="status">{verifyMessage}</p></div>;
  } else if (existing === undefined) {
    body = <div className="claim-dialog__loading"><div className="spinner" aria-label="Loading…" /></div>;
  } else if (existing?.status === 'pending') {
    body = (
      <div className="claim-dialog__status">
        <p className="claim-dialog__status-title">Your claim is being reviewed</p>
        <p>We usually get back to you within one to two business days. Once it's approved you can update the listing, add your socials and reply to reviews as the owner.</p>
        <button type="button" className="claim-dialog__submit" onClick={onClose}>Done</button>
      </div>
    );
  } else if (existing?.status === 'approved') {
    body = (
      <div className="claim-dialog__status">
        <p className="claim-dialog__status-title">This venue is yours</p>
        <Link to="/business/dashboard" className="claim-dialog__submit">Go to your dashboard</Link>
      </div>
    );
  } else {
    body = (
      <form onSubmit={submit} className="claim-dialog__form">
        {existing?.status === 'rejected' && (
          <p className="claim-dialog__rejected">
            Your last claim wasn't approved{existing.rejectionReason ? `: ${existing.rejectionReason}` : '.'} You can try again with more detail.
          </p>
        )}
        <p className="claim-dialog__intro">
          Tell us who you are and how we can confirm it. Once approved you can edit this listing, add your
          socials and reply to reviews as the owner. You can't edit or remove reviews.
        </p>
        <label>
          Your name
          <input value={form.contactName} onChange={set('contactName')} required maxLength={80} autoComplete="name" />
        </label>
        <label>
          Your role
          <select value={form.position} onChange={set('position')}>
            {POSITIONS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
        </label>
        <div className="claim-dialog__row">
          <label>
            Business phone
            <input value={form.phone} onChange={set('phone')} type="tel" maxLength={40} placeholder="(202) 555-0100" />
          </label>
          <label>
            Business email
            <input value={form.businessEmail} onChange={set('businessEmail')} type="email" maxLength={120} />
          </label>
        </div>
        <label>
          How can we confirm you run it? <span className="claim-dialog__optional">(optional)</span>
          <textarea value={form.note} onChange={set('note')} rows={3} maxLength={1000} placeholder="e.g. Call the venue and ask for Dana, or check our website's contact page." />
        </label>
        <label>
          Proof <span className="claim-dialog__optional">(optional — business licence, a bill in the venue's name, a photo of you behind the counter)</span>
          <input type="file" accept="image/jpeg,image/png,image/webp,image/heic,application/pdf" onChange={(e) => setProof(e.target.files?.[0] || null)} />
          <span className="claim-dialog__private">Only the Volcyy team can see this.</span>
        </label>
        {error && <p className="claim-dialog__error" role="alert">{error}</p>}
        <button type="submit" className="claim-dialog__submit" disabled={busy}>
          {busy ? 'Sending…' : 'Submit claim'}
        </button>
      </form>
    );
  }

  return (
    <div
      className="claim-dialog"
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="claim-dialog-title"
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
    >
      <div className={`claim-dialog__box${isStatusScreen ? ' claim-dialog__box--status' : ''}`}>
        <button type="button" className="claim-dialog__close" onClick={onClose} aria-label="Close">×</button>
        <h2 id="claim-dialog-title" className="claim-dialog__title">Claim {restaurant.name}</h2>
        {body}
      </div>
    </div>
  );
}

export function ClaimBadge({ restaurant }) { return <ClaimRestaurant restaurant={restaurant} badge />; }
